import {observer} from 'mobx-react-lite'
import React, {useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Backend} from '@common/backend'
import {LinkButtonNew} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {TablerIcons} from '@components/common/icon-tabler/icon-tabler'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {TwPage, TwTitle} from '@components/sticker/my-stickers/containers'
import {
  NewStickerType,
  useMyStickersStore
} from '@components/sticker/my-stickers/my-stickers-store'
import {getStickerIconByNewStickerType} from '@components/sticker/my-stickers/my-stickers-utils'
import {globals} from '@extensions/globals-base'
import {Player} from '@lottiefiles/react-lottie-player'

// HACK: lottie svg inside container has less height than auto height of the parent,
// so need to set parent's height equal to svg actual height after resize

const TwPlayer = tw(Player)`
  hidden
  group-hover:block
  h-[188px]
  rounded-16
`

const TwPlaceholder = tw.img`
  rounded-16
  overflow-hidden
  group-hover:hidden
  h-[188px]
`

interface NewStickerProps {
  id: NewStickerType
  icon: TablerIcons
  title: string
  description: string
  videoUrl: string
  placeholderUrl: string
}

const getStickerTypes = (): NewStickerProps[] => {
  const theme = globals.theme
  const lang = Lang.getLang()

  const res: NewStickerProps[] = []
  res.push({
    id: 'string',
    icon: getStickerIconByNewStickerType('string'),
    title: Lang.msg.myStickers.stickerWithStatusesTitle,
    description: Lang.msg.myStickers.stickerWithStatusesDescription,
    ...{
      en: {
        light: {
          videoUrl:
            '/media/video/stickers/anim-sticker-condition-eng-light-outlined.json',
          placeholderUrl:
            '/img/stickers/anim-sticker-condition-eng-light-cover.svg'
        },
        dark: {
          videoUrl:
            '/media/video/stickers/anim-sticker-condition-eng-dark-outlined.json',
          placeholderUrl:
            '/img/stickers/anim-sticker-condition-eng-dark-cover.svg'
        }
      },
      ru: {
        light: {
          videoUrl:
            '/media/video/stickers/anim-sticker-condition-ru-light-outlined.json',
          placeholderUrl:
            '/img/stickers/anim-sticker-condition-ru-light-cover.svg'
        },
        dark: {
          videoUrl:
            '/media/video/stickers/anim-sticker-condition-ru-dark-outlined.json',
          placeholderUrl:
            '/img/stickers/anim-sticker-condition-ru-dark-cover.svg'
        }
      }
    }[lang][theme]
  })
  res.push({
    id: 'sprint',
    icon: getStickerIconByNewStickerType('sprint'),
    title: Lang.msg.myStickers.stickerWithDateIntervalsTitle,
    description: Lang.msg.myStickers.stickerWithDateIntervalsDescription,
    ...{
      en: {
        light: {
          videoUrl: '/media/video/stickers/anim-sticker-sprint-eng-light.json',
          placeholderUrl:
            '/img/stickers/anim-sticker-sprint-eng-light-cover.svg'
        },
        dark: {
          videoUrl: '/media/video/stickers/anim-sticker-sprint-eng-dark.json',
          placeholderUrl: '/img/stickers/anim-sticker-sprint-eng-dark-cover.svg'
        }
      },
      ru: {
        light: {
          videoUrl: '/media/video/stickers/anim-sticker-sprint-ru-light.json',
          placeholderUrl: '/img/stickers/anim-sticker-sprint-ru-light-cover.svg'
        },
        dark: {
          videoUrl: '/media/video/stickers/anim-sticker-sprint-ru-dark.json',
          placeholderUrl: '/img/stickers/anim-sticker-sprint-ru-dark-cover.svg'
        }
      }
    }[lang][theme]
  })
  if (Backend.features?.numericAndTextStickers) {
    res.push({
      id: 'text',
      icon: getStickerIconByNewStickerType('text'),
      title: Lang.msg.myStickers.stickerWithTextTitle,
      description: Lang.msg.myStickers.stickerWithTextDescription,
      ...{
        en: {
          light: {
            videoUrl: '/media/video/stickers/anim-sticker-text-eng-light.json',
            placeholderUrl:
              '/img/stickers/anim-sticker-text-eng-light-cover.svg'
          },
          dark: {
            videoUrl: '/media/video/stickers/anim-sticker-text-eng-dark.json',
            placeholderUrl: '/img/stickers/anim-sticker-text-eng-dark-cover.svg'
          }
        },
        ru: {
          light: {
            videoUrl: '/media/video/stickers/anim-sticker-text-ru-light.json',
            placeholderUrl: '/img/stickers/anim-sticker-text-ru-light-cover.svg'
          },
          dark: {
            videoUrl: '/media/video/stickers/anim-sticker-text-ru-dark.json',
            placeholderUrl: '/img/stickers/anim-sticker-text-ru-dark-cover.svg'
          }
        }
      }[lang][theme]
    })
    res.push({
      id: 'numeric',
      icon: getStickerIconByNewStickerType('numeric'),
      title: Lang.msg.myStickers.stickerWithNumberTitle,
      description: Lang.msg.myStickers.stickerWithNumberDescription,
      ...{
        en: {
          light: {
            videoUrl:
              '/media/video/stickers/anim-sticker-number-eng-light-outlined.json',
            placeholderUrl:
              '/img/stickers/anim-sticker-number-eng-light-cover.svg'
          },
          dark: {
            videoUrl:
              '/media/video/stickers/anim-sticker-number-eng-dark-outlined.json',
            placeholderUrl:
              '/img/stickers/anim-sticker-number-eng-dark-cover.svg'
          }
        },
        ru: {
          light: {
            videoUrl:
              '/media/video/stickers/anim-sticker-number-ru-light-outlined.json',
            placeholderUrl:
              '/img/stickers/anim-sticker-number-ru-light-cover.svg'
          },
          dark: {
            videoUrl:
              '/media/video/stickers/anim-sticker-number-ru-dark-outlined.json',
            placeholderUrl:
              '/img/stickers/anim-sticker-number-ru-dark-cover.svg'
          }
        }
      }[lang][theme]
    })
  }
  return res
}

const NewStickerExample = observer((props: NewStickerProps) => {
  const {id, icon, title, description, videoUrl, placeholderUrl} = props
  const [, store] = useMyStickersStore()
  const ref = useRef<Player>(null)
  return (
    <div
      className="cursor-pointer flex flex-col group"
      onClick={() => store.startCreatingNewStickerWithType(id)}
      onMouseOver={() => ref.current?.play()}
      onMouseLeave={() => ref.current?.stop()}
    >
      <div className="flex gap-4 mb-8">
        <Icon icon={icon} color="secondary" />
        <LinkButtonNew title={title} />
      </div>
      <div className="flex-1 text-xs-regular text-secondary mb-8">
        {description}
      </div>
      <TwPlayer ref={ref} loop src={videoUrl} />
      <TwPlaceholder src={placeholderUrl} alt="" />
    </div>
  )
})

export const NewSticker = observer(() => {
  const [, store] = useMyStickersStore()

  return (
    <TwPage>
      <TwTitle className="pb-16">
        <LinkButtonNew
          icon="IconArrowLeft"
          title={Lang.msg.myStickers.backToBoardStickers}
          onClick={() => store.setCurrentTab('board')}
        />
        <LinkButtonNew
          icon="IconCustomExistingPlus"
          title={Lang.msg.myStickers.addStickerFromOtherBoard}
          onClick={() => store.setCurrentTab('library')}
          className="ml-auto"
        />
      </TwTitle>
      <div className="py-8 text-sm-semibold text-primary">
        {Lang.msg.myStickers.creatingSticker}
      </div>
      <WithPerfectScrollbar className="flex-1 pr-16 -mr-16">
        <div className="grid grid-cols-2 auto-rows-min gap-16">
          {getStickerTypes().map(props => (
            <NewStickerExample key={props.id} {...props} />
          ))}
        </div>
      </WithPerfectScrollbar>
    </TwPage>
  )
})
